import {
  DefaultButton,
  Dropdown,
  getTheme,
  IDropdownOption,
  Separator,
  Slider,
  Stack,
  StackItem,
  TextField
} from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import { Case, QueueConfiguration } from '../../services';
import { ReviewContent } from '../review/ReviewContent';
import { ReviewContextProvider } from '../review/ReviewContextProvider';
import { Default_Cases } from './sampleCases';
import { sampleJsonOptions } from './sampleJsonContentTypes';

import beautify from 'json-beautify';
import { FieldSectionComponent } from '../admin/FieldSectionComponent';
import { separatorStyle } from '../admin/SettingsStyles';
import { CardHeightField } from '../admin/CardHeightField';
import { AdminContext } from '../../Store/Admin';
import { DEFAULT_MAX_MEDIA_HEIGHT, MediaHeightField } from '../admin/MediaHeightField';
import { MaxCasesPerSet } from '../../components/admin/rules/ConfigCaseField';
import {
  AnimationLoopSpeedField,
  DEFAULT_MAX_ANIMATION_LOOP_SPEED,
  DEFAULT_MIN_ANIMATION_LOOP_SPEED
} from '../admin/AnimationLoopSpeedField';
import { AnimationLoopToggleField } from '../admin/AnimationLoopToggleField';

interface LiveEditProps {
  config: QueueConfiguration;
}

export const LiveEdit: React.FC<LiveEditProps> = props => {
  const { state, dispatch } = React.useContext(AdminContext);
  const [subjectValue, setSubjectValue] = useState('');
  const [error, setError] = useState('');
  const [selectedJsonContentTypeOption, setSelectedJsonContentTypeOption] = useState<IDropdownOption>();
  const { palette } = getTheme();

  const [cases, setCases] = useState<Case[]>();

  useEffect(() => {
    if (subjectValue) {
      const tempNewCase = { ...Default_Cases[0] };

      try {
        // @ts-ignore
        tempNewCase.subject.subjectValue = beautify(JSON.parse(subjectValue), null, 4, 60);
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        setError(`The JSON Content is in a bad format: ${err}`);
        return;
      }

      const newCases: Case[] = [];
      let newCaseId = 0;
      while (newCases.length < (state.queueDataState?.defaultMaxAssignedCaseCount || 5)) {
        const newCase = { ...tempNewCase };
        newCase.id = newCaseId.toString();
        // @ts-ignore
        newCases.push(newCase);
        newCaseId++;
      }
      setError('');
      setCases(newCases);
    }
  }, [subjectValue, state.queueDataState?.defaultMaxAssignedCaseCount]);

  const addSelectedSection = () => {
    if (selectedJsonContentTypeOption) {
      let subjectValueJson;
      if (subjectValue) {
        try {
          subjectValueJson = JSON.parse(subjectValue);
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          setError(`subject value failed to parse: ${error}`);
          return;
        }
        try {
          subjectValueJson.output.push(selectedJsonContentTypeOption.data);
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          setError(`subject value does not have output array: ${error}`);
          return;
        }
      } else {
        subjectValueJson = { output: [selectedJsonContentTypeOption.data] };
      }

      // @ts-ignore
      setSubjectValue(beautify(subjectValueJson, null, 4, 60));
    }
  };

  const updateCountDisplayed = (newCount: number) => {
    dispatch({
      type: 'DEFAULT_MAX_ASSIGNED_CASE_COUNT',
      payload: newCount
    });
    dispatch({
      type: 'IS_UPDATED',
      payload: true
    });
  };

  return (
    <ReviewContextProvider>
      <Stack className="h-100" horizontal style={{ justifyContent: 'space-between' }}>
        <Stack style={{ borderRight: '1px solid grey', padding: 25 }}>
          <h2>Create Sample JSON Content</h2>
          <Stack horizontal>
            <Dropdown
              style={{ width: 200, marginRight: 20 }}
              ariaLabel="Content types"
              options={sampleJsonOptions}
              onChange={(evt, option) => setSelectedJsonContentTypeOption(option)}
            />
            <DefaultButton onClick={addSelectedSection}>Add section</DefaultButton>
          </Stack>

          <TextField
            label="Enter data here:"
            placeholder="Copy your Subject Value into this text box, or construct it using the dropdown above."
            multiline
            rows={15}
            onChange={(e, newValue) => setSubjectValue(newValue ?? '')}
            value={subjectValue}
            style={{ width: 400, height: 200 }}
          />
          <h2 style={{ marginTop: 20 }}>UX Preferences</h2>
          <FieldSectionComponent
            title={'Card Height'}
            body={'Set the height of the card in pixels. Minimum 100, maximum 600.'}
          >
            <Stack.Item>
              <CardHeightField tooltip={''} id="cardHeight" label="" />
            </Stack.Item>
          </FieldSectionComponent>
          <FieldSectionComponent
            title={'Media Height'}
            body={`Set the height of images and videos in non-card views. No min, maximum ${DEFAULT_MAX_MEDIA_HEIGHT}. If set to zero or undefined they will display at 500px.`}
          >
            <Stack.Item>
              <MediaHeightField tooltip={''} id="mediaHeight" label="" />
            </Stack.Item>
          </FieldSectionComponent>
          <Separator styles={separatorStyle(palette)} />
          <Slider
            label="Count to show"
            min={1}
            max={MaxCasesPerSet}
            step={1}
            value={state.queueDataState?.defaultMaxAssignedCaseCount}
            onChange={val => updateCountDisplayed(val)}
            showValue
            snapToStep
          />
          <Separator styles={separatorStyle(palette)} />
          <FieldSectionComponent
            title={`Animation Loop`}
            id={'AnimationLoopTitle'}
            body={`Set the carousel animation loop, in seconds. This will configure the number of seconds that should transpire between the following image. Minimum ${DEFAULT_MIN_ANIMATION_LOOP_SPEED} seconds, maximum ${DEFAULT_MAX_ANIMATION_LOOP_SPEED} seconds.`}
          >
            <Stack.Item>
              <AnimationLoopToggleField
                describedBy="AnimationLoopTitle"
                tooltip={''}
                id="animationLoopToggle"
                label=""
              />
              <AnimationLoopSpeedField tooltip={''} id="animationLoopSpeed" label="Speed in seconds" />
            </Stack.Item>
          </FieldSectionComponent>
        </Stack>
        <Stack className="h-100 w-100" style={{ padding: 25 }}>
          <StackItem grow>
            <Stack horizontal className="h-100">
              {!error ? (
                <StackItem grow>
                  {cases && (
                    <>
                      <ReviewContent config={props.config} cases={cases} />
                      <span>
                        Note: Blur/Desaturate/Flip are used here only for display purposes, change those settings in the
                        Content previews tab.
                      </span>
                    </>
                  )}
                </StackItem>
              ) : (
                <h2>{error}</h2>
              )}
            </Stack>
          </StackItem>
        </Stack>
      </Stack>
    </ReviewContextProvider>
  );
};
